import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Hero-slot card: the previous drop is still buyable for 24 hours after
 * it ends, at the exact tier price it locked at during the live. Sits ABOVE
 * the fold on Landing and Home hero so a returning visitor sees "Missed
 * last night? Grab it before it's gone." without scrolling.
 *
 * Props:
 *   drop          — full live doc from GET /lives/last-buyable
 *   isAuthed      — true when a signed-in Founding member is viewing
 *   onSignInHint  — CTA click handler for anonymous visitors (opens sign-in)
 *   defaultAddr   — user's default shipping address (for logged-in buy)
 *   compact       — narrower layout for the Landing hero right column
 */
export default function LastDropCard({ drop, isAuthed, onSignInHint, defaultAddr, compact = false }) {
  const [remaining, setRemaining] = useState(computeRemaining(drop));
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    if (!drop?.buyable_until) return undefined;
    const id = setInterval(() => setRemaining(computeRemaining(drop)), 30000);
    return () => clearInterval(id);
  }, [drop?.buyable_until, drop]);

  if (!drop) return null;

  const price = drop.final_locked_price ?? drop.current_price ?? 0;
  const basePrice = drop.product?.base_price ?? 0;
  const stock = drop.product?.stock ?? 0;
  const savings = basePrice > price ? Math.round(((basePrice - price) / basePrice) * 100) : 0;
  const soldOut = stock <= 0;

  const buyNow = async () => {
    if (!isAuthed) { onSignInHint?.(); return; }
    if (!defaultAddr) {
      toast.error("Add a shipping address in your profile first", { duration: 4500 });
      return;
    }
    if (!window.Razorpay) { toast.error("Payment SDK still loading — try again in a second."); return; }
    setBooking(true);
    try {
      const r = await api.post(`/bookings`, { live_id: drop.live_id, quantity: 1 });
      const bkg = r.data;
      const options = {
        key: bkg.razorpay_key_id,
        amount: bkg.razorpay_amount,
        currency: "INR",
        name: "Drop Shop Live",
        description: drop.title || drop.product?.name || "Last drop purchase",
        order_id: bkg.razorpay_order_id,
        prefill: { name: "", email: "", contact: defaultAddr.phone || "" },
        notes: { booking_id: bkg.booking_id, ships_to: `${defaultAddr.city} ${defaultAddr.postal_code}` },
        theme: { color: "#FF6A00" },
        handler: async (resp) => {
          try {
            await api.post(`/payments/verify`, {
              booking_id: bkg.booking_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`Paid ₹${bkg.total}! Order ${bkg.booking_id}`, { duration: 6000 });
          } catch (err) { toast.error(err.response?.data?.detail || "Payment verification failed"); }
        },
      };
      new window.Razorpay(options).open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    } finally { setBooking(false); }
  };

  return (
    <div
      className={`relative border-2 border-[#161616] bg-[#FFFFFF] shadow-[6px_6px_0_#FF6A00] overflow-hidden ${compact ? "p-4" : "p-5"}`}
      data-testid="last-drop-card"
    >
      {/* Header ribbon */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="brand-tag text-[#FF6A00] truncate">// Last drop · still buyable</div>
        <div
          className="shrink-0 font-mono-ds text-[9px] uppercase tracking-[0.2em] tabular-nums px-1.5 py-0.5 border border-[#FF6A00] text-[#FF6A00] font-bold"
          data-testid="last-drop-window"
        >
          {remaining} left
        </div>
      </div>

      <div className={`flex ${compact ? "flex-col gap-3" : "gap-4 items-center"}`}>
        {drop.product?.image_url && (
          <img
            src={drop.product.image_url}
            alt={drop.product?.name || "Last drop"}
            className={`object-cover border border-[#E5E1D8] shrink-0 ${compact ? "w-full h-36" : "w-24 h-24"}`}
            data-testid="last-drop-image"
          />
        )}

        <div className="flex-1 min-w-0">
          <div
            className="font-display uppercase tracking-tight text-base md:text-lg leading-tight line-clamp-2"
            data-testid="last-drop-title"
            title={drop.product?.name || drop.title || "Last drop"}
          >
            {drop.product?.name || drop.title || "Last drop"}
          </div>
          <div className="flex items-baseline gap-2 mt-2 flex-wrap">
            <span className="font-display text-3xl text-[#FF6A00] font-bold tabular-nums leading-none" data-testid="last-drop-price">
              ₹{Number(price).toLocaleString("en-IN")}
            </span>
            {basePrice > price && (
              <>
                <span className="font-mono-ds text-xs text-[#6E6E6E] line-through tabular-nums">₹{Number(basePrice).toLocaleString("en-IN")}</span>
                {savings > 0 && (
                  <span className="font-mono-ds text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[#B9E641] text-[#161616] font-bold">−{savings}%</span>
                )}
              </>
            )}
          </div>
          <div className="mt-1.5 font-mono-ds text-[10px] uppercase tracking-[0.20em] text-[#6E6E6E]">
            {soldOut ? "Sold out at floor" : `${stock} left · locked from last night's live`}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <button
          onClick={buyNow}
          disabled={booking || soldOut}
          className="btn-urgent h-11 w-full inline-flex items-center justify-center whitespace-nowrap text-[12px] leading-none disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="last-drop-buy-btn"
        >
          {soldOut ? "Sold Out" : booking ? "Processing…" : (isAuthed ? `Buy at ₹${Number(price).toLocaleString("en-IN")} →` : "Sign in to grab it →")}
        </button>
      </div>
    </div>
  );
}

function computeRemaining(drop) {
  if (!drop?.buyable_until) return "—";
  const diff = new Date(drop.buyable_until).getTime() - Date.now();
  if (diff <= 0) return "expiring";
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}
